import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Heart, Thermometer, Wind, Activity } from "lucide-react";

interface VitalSigns {
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
}

interface VitalSignsInputProps {
  values: VitalSigns;
  onChange: (values: VitalSigns) => void;
}

export function VitalSignsInput({ values, onChange }: VitalSignsInputProps) {
  const updateValue = (key: keyof VitalSigns, value: string) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    onChange({ ...values, [key]: numValue });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Sinais Vitais</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bp_systolic">PA Sistólica (mmHg)</Label>
          <Input
            id="bp_systolic"
            type="number"
            placeholder="120"
            value={values.blood_pressure_systolic || ""}
            onChange={(e) => updateValue("blood_pressure_systolic", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bp_diastolic">PA Diastólica (mmHg)</Label>
          <Input
            id="bp_diastolic"
            type="number"
            placeholder="80"
            value={values.blood_pressure_diastolic || ""}
            onChange={(e) => updateValue("blood_pressure_diastolic", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="heart_rate" className="flex items-center gap-1">
            <Heart className="h-3 w-3" />
            FC (bpm)
          </Label>
          <Input
            id="heart_rate"
            type="number"
            placeholder="72"
            value={values.heart_rate || ""}
            onChange={(e) => updateValue("heart_rate", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="respiratory_rate" className="flex items-center gap-1">
            <Wind className="h-3 w-3" />
            FR (rpm)
          </Label>
          <Input
            id="respiratory_rate"
            type="number"
            placeholder="16"
            value={values.respiratory_rate || ""}
            onChange={(e) => updateValue("respiratory_rate", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="temperature" className="flex items-center gap-1">
            <Thermometer className="h-3 w-3" />
            Temperatura (°C)
          </Label>
          <Input
            id="temperature"
            type="number"
            step="0.1"
            placeholder="36.5"
            value={values.temperature || ""}
            onChange={(e) => updateValue("temperature", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="oxygen_saturation" className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            SpO2 (%)
          </Label>
          <Input
            id="oxygen_saturation"
            type="number"
            placeholder="98"
            value={values.oxygen_saturation || ""}
            onChange={(e) => updateValue("oxygen_saturation", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="weight">Peso (kg)</Label>
          <Input
            id="weight"
            type="number"
            step="0.1"
            placeholder="70"
            value={values.weight || ""}
            onChange={(e) => updateValue("weight", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="height">Altura (m)</Label>
          <Input
            id="height"
            type="number"
            step="0.01"
            placeholder="1.75"
            value={values.height || ""}
            onChange={(e) => updateValue("height", e.target.value)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
